import React, { useState } from 'react';
import { Icons } from 'components/Icons';
import { InfraHexMeshCanvas } from './diagrams/InfraHexMeshCanvas';
import { StrengthsTabPanels } from './strengths/StrengthsTabPanels';

const STRENGTH_TABS = ['platform', 'infra', 'labs', 'ontology'];

const TAGLINE_INDEX = {
  platform: 0,
  infra: 1,
  labs: 2,
  ontology: 3,
};

const PILLAR_HEADLINE_DEFAULTS = {
  platform: 'Platform',
  infra: 'Infra',
  labs: 'Labs',
  ontology: 'Ontology',
};

/**
 * Section 03: group strengths with four pillars (Platform, Infra, Labs, Ontology).
 */
export function InvestorsStrengthsSection({ t, besperAiLabUrl }) {
  const [strengthsTab, setStrengthsTab] = useState('platform');

  const stripPct = (TAGLINE_INDEX[strengthsTab] * 100) / STRENGTH_TABS.length;
  const pillarForA11y =
    strengthsTab === 'platform'
      ? t('strengths.platform.headline', PILLAR_HEADLINE_DEFAULTS.platform)
      : strengthsTab === 'infra'
        ? t('strengths.infra.headline', PILLAR_HEADLINE_DEFAULTS.infra)
        : strengthsTab === 'labs'
          ? t('strengths.labs.headline', PILLAR_HEADLINE_DEFAULTS.labs)
          : t('strengths.ontology.headline', PILLAR_HEADLINE_DEFAULTS.ontology);

  return (
    <section
      className={`investors-section investors-strengths investors-strengths-active-${strengthsTab}`}
      aria-label={t('strengths.title', 'Our strengths')}
    >
      <div className="investors-strengths-bg-layer investors-strengths-bg-platform" aria-hidden="true" />
      <div className="investors-strengths-bg-layer investors-strengths-bg-infra" aria-hidden="true" />
      {strengthsTab === 'infra' ? (
        <InfraHexMeshCanvas className="investors-strengths-infra-hex-canvas" />
      ) : null}
      <div className="investors-strengths-bg-layer investors-strengths-bg-labs" aria-hidden="true" />
      <div className="investors-strengths-bg-layer investors-strengths-bg-ontology" aria-hidden="true" />
      <div className="investors-bleed-inner investors-strengths-inner">
        <div className="investors-strengths-header-row">
          <div className="investors-strengths-header-left">
            <span className="investors-section-overline investors-strengths-overline" aria-hidden="true">03</span>
            <div className="investors-strengths-headline-tagline">
              <div
                className="investors-strengths-tagline-row"
                aria-label={`B-esper ${pillarForA11y}`}
              >
                <div className="investors-strengths-tagline-lockup" aria-hidden="true">
                  <div className="bsp-logo-wrapper">
                    <div className="bsp-logo-circle">
                      <span className="bsp-logo-letter">B</span>
                    </div>
                    <span className="bsp-logo-text">esper</span>
                  </div>
                </div>
                <span className="investors-strengths-tagline-row-rule" aria-hidden="true" />
                <div className="investors-strengths-tagline-viewport" aria-live="polite">
                  <div
                    className="investors-strengths-tagline-strip"
                    style={{
                      transform: `translateY(-${stripPct}%)`,
                    }}
                  >
                    <span className="investors-strengths-tagline-item investors-strengths-tagline-pillar">
                      {t('strengths.platform.headline', PILLAR_HEADLINE_DEFAULTS.platform)}
                    </span>
                    <span className="investors-strengths-tagline-item investors-strengths-tagline-pillar">
                      {t('strengths.infra.headline', PILLAR_HEADLINE_DEFAULTS.infra)}
                    </span>
                    <span className="investors-strengths-tagline-item investors-strengths-tagline-pillar">
                      {t('strengths.labs.headline', PILLAR_HEADLINE_DEFAULTS.labs)}
                    </span>
                    <span className="investors-strengths-tagline-item investors-strengths-tagline-pillar">
                      {t('strengths.ontology.headline', PILLAR_HEADLINE_DEFAULTS.ontology)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className={`investors-strengths-tabs investors-strengths-tabs--${strengthsTab}`}
            data-active-tab={strengthsTab}
            role="tablist"
            aria-label={t('strengths.title', 'Our strengths')}
          >
            <button
              type="button"
              role="tab"
              aria-selected={strengthsTab === 'platform'}
              aria-controls="investors-strengths-panel"
              id="investors-strengths-tab-platform"
              className={`investors-strengths-tab ${strengthsTab === 'platform' ? 'investors-strengths-tab-active' : ''}`}
              onClick={() => setStrengthsTab('platform')}
            >
              <span className="investors-strengths-tab-icon" aria-hidden="true"><Icons.Dashboard size={18} /></span>
              {t('strengths.tabPlatform', 'Platform')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={strengthsTab === 'infra'}
              aria-controls="investors-strengths-panel"
              id="investors-strengths-tab-infra"
              className={`investors-strengths-tab ${strengthsTab === 'infra' ? 'investors-strengths-tab-active' : ''}`}
              onClick={() => setStrengthsTab('infra')}
            >
              <span className="investors-strengths-tab-icon" aria-hidden="true"><Icons.Server size={18} /></span>
              {t('strengths.tabInfra', 'Infra')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={strengthsTab === 'labs'}
              aria-controls="investors-strengths-panel"
              id="investors-strengths-tab-labs"
              className={`investors-strengths-tab ${strengthsTab === 'labs' ? 'investors-strengths-tab-active' : ''}`}
              onClick={() => setStrengthsTab('labs')}
            >
              <span className="investors-strengths-tab-icon" aria-hidden="true"><Icons.Chart size={18} /></span>
              {t('strengths.tabLabs', 'Labs')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={strengthsTab === 'ontology'}
              aria-controls="investors-strengths-panel"
              id="investors-strengths-tab-ontology"
              className={`investors-strengths-tab ${strengthsTab === 'ontology' ? 'investors-strengths-tab-active' : ''}`}
              onClick={() => setStrengthsTab('ontology')}
            >
              <span className="investors-strengths-tab-icon" aria-hidden="true"><Icons.Network size={18} /></span>
              {t('strengths.tabOntology', 'Ontology')}
            </button>
          </div>
        </div>
        <div
          id="investors-strengths-panel"
          role="tabpanel"
          aria-labelledby={`investors-strengths-tab-${strengthsTab}`}
          className="investors-strengths-panel"
        >
          <div className="investors-strengths-panel-content">
            <StrengthsTabPanels t={t} besperAiLabUrl={besperAiLabUrl} strengthsTab={strengthsTab} />
          </div>
        </div>
      </div>
    </section>
  );
}
